<script setup lang="ts">
const { isMobile } = useWindowResize();
</script>
<template>
  <div class="page page-terms-of-service">
    <TermsOfServiceMenu />
    <h1 class="title">
      Cookie Policy
    </h1>
    <p>
      Peatix Inc. ("Peatix", "we", "us", "our") uses cookies to help us provide you with a good
      experience when using our site as well as to enable us to make improvements to our site. We
      use cookies to keep you signed in, remember your user preferences, enable social media
      features, analyze website traffic and more.
    </p>
    <article>
      <h2 class="article-title">
        What are Cookies
      </h2>
      <p>
        Cookies are small text files generated by websites that you visit, which are stored by the
        browser on your computer or mobile device. The cookies that are set by us are called
        first-party cookies. We also use third-party cookies, which are cookies placed by a website
        other than the one you are visiting. Cookies may be either session cookies or persistent
        cookies. Session cookies are only active during a session and will be deleted once the
        browser is closed. Persistent cookies on the other hand remain on your device even after the
        browser is closed; they will expire after a specific length of time, unless deleted by the
        user before its set expiration date.
      </p>
      <Divider />
    </article>
    <article>
      <h2 class="article-title">
        Use of Cookies by Peatix
      </h2>
      <p>
        The cookies that we use are grouped into the following 5 categories based on their function
        or purpose: (1) Strictly Necessary Cookies, (2) Functional Cookies, (3) Performance Cookies,
        (4) Targeting Cookies, and (5) Social Media Cookies. More information about the cookies we
        use, and the purpose for which we use them, are detailed below.
      </p>
      <Divider />
    </article>
    <article>
      <h2 class="article-title">
        Strictly Necessary Cookies
      </h2>
      <p>
        These cookies are necessary for the website to function and cannot be switched off in our
        systems. They are usually only set in response to actions made by you which amount to a
        request for services, such as setting your privacy preferences, signing in or filling in
        forms. You can set your browser to block or alert you about these cookies, but some parts of
        the site may not work then. These cookies do not store any personally identifiable
        information.
      </p>
      <VTable class="table-cookie-policy">
        <thead>
          <tr>
            <template v-if="isMobile">
              <th colspan="4">
                <div class="header-sm">
                  <span>Service</span><span>/</span> <span>Cookie</span><span>/</span>
                  <span>Description</span><span>/</span>
                  <span>Opt-out</span>
                </div>
              </th>
            </template>
            <template v-else>
              <th><span>Service</span></th>
              <th><span>Cookie</span></th>
              <th><span>Description</span></th>
              <th><span>Opt-out</span></th>
            </template>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Google reCAPTCHA</td>
            <td>
              <div class="col-cookie">
                <p>rc::a</p>
                <p>rc::b</p>
                <p>rc::c</p>
                <p>_GRECAPTCHA</p>
              </div>
            </td>
            <td>
              <div>
                These cookies are used to distinguish between humans and bots. They help to protect
                our site from spam and abuse. See
                <a
                  targe="_blank"
                  href="https://policies.google.com/privacy"
                >Google's Privacy Policy</a>
              </div>
            </td>
            <td>-</td>
          </tr>
          <tr>
            <td>Google Tag Manager</td>
            <td class="col-cookie">
              <p>_dc_gtm_UA-#</p>
            </td>
            <td>
              This cookie is used by Google Tag Manager to support Google Analytics on the site. It
              helps us monitor the use and performance of our website.
            </td>
            <td>-</td>
          </tr>
          <tr>
            <td>IAB TCF</td>
            <td class="col-cookie">
              <p>eupubconsent</p>
            </td>
            <td>
              This cookie is used by the IAB Europe Transparency & Consent Framework to store the
              user’s consent to the data collection purposes. The cookie holds an encrypted consent
              string that vendors participating in the framework can read and determine the user’s
              consent.
            </td>
            <td>-</td>
          </tr>
          <tr>
            <td><span>OneTrust</span></td>
            <td>
              <div class="col-cookie">
                <p>OptanonConsent</p>
                <p>OptanonAlertBoxClosed</p>
              </div>
            </td>
            <td>
              <div>
                These cookies are set by the cookie compliance solution from OneTrust. It stores
                information about the categories of cookies the site uses and whether visitors have
                given or withdrawn consent for the use of each category. This enables Peatix to
                prevent cookies in each category from being set in the user’s browser, when consent
                is not given. It contains no information that can identify the site visitor. See
                <a
                  target="_blank"
                  href="https://www.onetrust.com/privacy-notice/"
                >OneTrust's Privacy Policy</a>
              </div>
            </td>
            <td>-</td>
          </tr>
          <tr>
            <td>Peatix</td>
            <td>
              <div class="col-cookie">
                <p>OptanonConsent</p>
                <p>OptanonAlertBoxClosed</p>
              </div>
            </td>
            <td>
              Peatix uses these cookies for session management. They are needed to enable users to
              remain signed-in to their accounts as they navigate the site, as well as to browse the
              site in their selected language.
            </td>
            <td>-</td>
          </tr>
          <tr>
            <td>PHP</td>
            <td>PHPSESSID</td>
            <td>
              This cookie is used to manage a user's session on the site. It stores and tracks data
              of a user as they navigate the site and gets deleted when all browser windows are
              closed.
            </td>
            <td>-</td>
          </tr>
          <tr>
            <td>Stripe</td>
            <td>
              <div class="col-cookie">
                <p>m</p>
                <p>__stripe_mid</p>
                <p>__stripe_sid</p>
              </div>
            </td>
            <td>
              This cookie is used to manage a user's session on the site. It stores and tracks data
              of a user as they navigate the site and gets deleted when all browser windows are
              closed.
            </td>
            <td>-</td>
          </tr>
        </tbody>
      </VTable>
      <Divider />
    </article>
    <article>
      <h2 class="article-title">
        Functional Cookies
      </h2>
      <p>
        These cookies enable the website to provide enhanced functionality and personalization. They
        may be set by us or by third-party providers whose services may have been added to our
        pages. If you do not allow these cookies, then some, or all, of these services may not
        function properly.
      </p>
      <VTable class="table-cookie-policy">
        <thead>
          <tr>
            <template v-if="isMobile">
              <th colspan="4">
                <div class="header-sm">
                  <span>Service</span><span>/</span> <span>Cookie</span><span>/</span>
                  <span>Description</span><span>/</span>
                  <span>Opt-out</span>
                </div>
              </th>
            </template>
            <template v-else>
              <th><span>Service</span></th>
              <th><span>Cookie</span></th>
              <th><span>Description</span></th>
              <th><span>Opt-out</span></th>
            </template>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>ExpressionEngine</td>
            <td>
              <div class="col-cookie">
                <p>exp_last_activity</p>
                <p>exp_last_visit</p>
                <p>exp_tracker</p>
                <p>exp_stashid</p>
              </div>
            </td>
            <td>
              <div>
                Peatix uses the ExpressionEngine content management system for a number of landing
                pages. ExpressionEngine uses cookies to track a user's session - when you last
                visited or were active on our site. No personal information is stored in these
                cookies, only timestamps related to that browser session. See
                <a
                  targe="_blank"
                  href="https://expressionengine.com/about/privacy-policy"
                >Expression Engine's Privacy Policy</a>
              </div>
            </td>
            <td>Through your browser settings</td>
          </tr>
          <tr>
            <td>Facebook</td>
            <td>
              <div class="col-cookie">
                <p>fbsr_#</p>
              </div>
            </td>
            <td>
              <div>
                This cookie is set by Facebook when users choose to connect their Facebook account
                with their Peatix account via social login. See
                <a
                  targe="_blank"
                  href="https://www.facebook.com/privacy/policy/?entry_point=data_policy_redirect&entry=0"
                >Facebook's Data Policy</a>
              </div>
            </td>
            <td>
              <a
                href="https://www.facebook.com/privacy/explanation"
                target="_blank"
              >https://www.facebook.com/privacy/explanation</a>
            </td>
          </tr>
          <tr>
            <td>Freshworks</td>
            <td>
              <div class="col-cookie">
                <p>_helpkit_session</p>
                <p>_x_w</p>
                <p>_zg</p>
              </div>
            </td>
            <td>
              <div>
                Peatix uses the customer service software Freshdesk to provide customer support to
                users, as well as to create and manage help pages. These cookies are set to track
                your session when interacting with Freshdesk. If you do not accept these cookies,
                some support functionalities might not work, and you might not be able to receive
                support for your inquiries. See
                <a
                  targe="_blank"
                  href="https://www.freshworks.com/privacy/"
                >Freshworks' Privacy Policy</a>
              </div>
            </td>
            <td>Through your browser settings</td>
          </tr>
          <tr>
            <td>Peatix</td>
            <td>
              <div class="col-cookie">
                <p>selected</p>
              </div>
            </td>
            <td>
              <div>
                This cookie remembers a signed-in user's last clicked tab on the user dashboard. Its
                purpose is to help users quickly navigate to parts of the site that are most
                relevant to them, such as to their tickets or events that they are organizing.
              </div>
            </td>
            <td>Through your browser settings</td>
          </tr>
          <tr>
            <td>Calculated fields</td>
            <td>
              <div class="col-cookie">
                <p>CP5XKN6QLDFWUC</p>
              </div>
            </td>
            <td>
              <div>
                Session cookie used by the online calculators on the site. Without the calculated
                fields cookie, the online calculators may not work.
              </div>
            </td>
            <td>Through your browser settings</td>
          </tr>
          <tr>
            <td>SurveyMonkey</td>
            <td>
              <div class="col-cookie">
                <p>ep201</p>
                <p>ep202</p>
                <p>endpage</p>
                <p>wuentry</p>
                <p>ep203</p>
                <p>apex__sm</p>
                <p>sm_rec</p>
                <p>auth</p>
              </div>
            </td>
            <td>
              <div>
                Peatix embeds Wufoo web forms onto several of our landing pages. The forms are used
                to collect and manage applications for certain campaigns or programs on Peatix. If
                you fill out any of these forms, Wufoo saves cookies into your computer to track
                your progress and to help us identify how you use the form.
              </div>
            </td>
            <td>Through your browser settings</td>
          </tr>
          <tr>
            <td>Wordpress</td>
            <td>
              <div class="col-cookie">
                <p>ip2location_redirection_first_visit</p>
                <p>wordpress_google_apps_login</p>
              </div>
            </td>
            <td>
              <div>
                Peatix uses Wordpress for several of our landing pages and our blog. These cookies
                are a functional cookie used for Wordpress.
              </div>
            </td>
            <td>Through your browser settings</td>
          </tr>
          <tr>
            <td>Zoho</td>
            <td>
              <div class="col-cookie">
                <p>Crmcsr</p>
                <p>LS_CSRF_TOKEN</p>
                <p>663a60c55d</p>
                <p>6e4b8efee4</p>
                <p>HttpOnly</p>
                <p>_zcsr_tmp</p>
              </div>
            </td>
            <td>
              <div>
                Peatix uses Zoho for customer relationship management. These cookies are used to
                identify users across devices and visits and to collect information on user
                preferences in order to optimize a user's experience when interacting with Zoho
                features on the website. The cookies are also used for security purposes - to
                identify trusted web traffic and for form security.
              </div>
            </td>
            <td>Through your browser settings</td>
          </tr>
        </tbody>
      </VTable>
      <Divider />
    </article>
    <article>
      <h2 class="article-title">
        Performance Cookies
      </h2>
      <p>
        These cookies allow us to count visits and traffic sources, so we can measure and improve
        the performance of our site. They help us to know which pages are the most and least popular
        and see how visitors move around the site. All information collected by these cookies is
        aggregated and therefore anonymous. If you do not allow these cookies we will not know when
        you have visited our site and will not be able to monitor its performance.
      </p>
      <VTable class="table-cookie-policy">
        <thead>
          <tr>
            <template v-if="isMobile">
              <th colspan="4">
                <div class="header-sm">
                  <span>Service</span><span>/</span> <span>Cookie</span><span>/</span>
                  <span>Description</span><span>/</span>
                  <span>Opt-out</span>
                </div>
              </th>
            </template>
            <template v-else>
              <th><span>Service</span></th>
              <th><span>Cookie</span></th>
              <th><span>Description</span></th>
              <th><span>Opt-out</span></th>
            </template>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Google Analytics</td>
            <td>
              <div class="col-cookie">
                <p>collect</p>
              </div>
            </td>
            <td>
              <div>
                This cookie is used to send data to Google Analytics about the user's device and
                behavior. It tracks the user across devices and marketing channels.
              </div>
            </td>
            <td>
              <a
                href="https://tools.google.com/dlpage/gaoptout"
                target="_blank"
              >https://tools.google.com/dlpage/gaoptout</a>
            </td>
          </tr>
          <tr>
            <td>Google Analytics</td>
            <td>
              <div class="col-cookie">
                <p>_ga</p>
                <p>_ga_XSPLBTE995</p>
                <p>_gat</p>
                <p>_gat_UA-nnnnnnn-nn</p>
                <p>_gid</p>
                <p>peatixall</p>
                <p>peatixall_gid</p>
              </div>
            </td>
            <td>
              <div>
                These cookies are used to collect information about how visitors use our website. We
                use the information to compile reports and to help us improve the website. The
                cookies collect information in a way that does not directly identify anyone,
                including the number of visitors to the website, where visitors have come to the
                website from, and the pages they visited.
              </div>
            </td>
            <td>
              <a
                href="https://tools.google.com/dlpage/gaoptout"
                target="_blank"
              >https://tools.google.com/dlpage/gaoptout</a>
            </td>
          </tr>
          <tr>
            <td>Google conversion</td>
            <td>
              <div class="col-cookie">
                <p>_gclxxxx</p>
              </div>
            </td>
            <td>
              <div>A tracking cookie for Google Conversion.</div>
            </td>
            <td>
              <a
                href="https://tools.google.com/dlpage/gaoptout"
                target="_blank"
              >https://tools.google.com/dlpage/gaoptout</a>
            </td>
          </tr>
          <tr>
            <td>Peatix</td>
            <td>
              <div class="col-cookie">
                <p>__ukwlgck</p>
              </div>
            </td>
            <td>
              <div>
                This cookie collects information about how users interact with the events on our
                site, which may enable us to improve the search results on Peatix Search.
              </div>
            </td>
            <td>Through your browser settings</td>
          </tr>
          <tr>
            <td>Vimeo</td>
            <td>
              <div class="col-cookie">
                <p>player</p>
                <p>vuid</p>
                <p>__cf_bm</p>
              </div>
            </td>
            <td>
              <div>
                Peatix enables organizers to embed Vimeo videos onto their group pages to enrich the
                content on those pages. Organizers can also embed Vimeo videos onto the Watch Stream
                page of their online events, accessible to only signed-in registered attendees. This
                cookie collects anonymous information about users' browsing sessions. See
                <a
                  href="https://vimeo.com/privacy"
                  target="_blank"
                >Vimeo's Privacy Policy</a>
              </div>
            </td>
            <td>Through your browser settings</td>
          </tr>
        </tbody>
      </VTable>
      <Divider />
    </article>
    <article>
      <h2 class="article-title">
        Targeting Cookies
      </h2>
      <p>
        These cookies may be set through our site by our advertising partners. They may be used by
        those companies to build a profile of your interests and show you relevant advertisements on
        other sites. They do not store directly personal information, but are based on uniquely
        identifying your browser and internet device. If you do not allow these cookies, you will
        experience less targeted advertising.
      </p>
      <VTable class="table-cookie-policy">
        <thead>
          <tr>
            <template v-if="isMobile">
              <th colspan="4">
                <div class="header-sm">
                  <span>Service</span><span>/</span> <span>Cookie</span><span>/</span>
                  <span>Description</span><span>/</span>
                  <span>Opt-out</span>
                </div>
              </th>
            </template>
            <template v-else>
              <th><span>Service</span></th>
              <th><span>Cookie</span></th>
              <th><span>Description</span></th>
              <th><span>Opt-out</span></th>
            </template>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>cdn.syndication.twimg.com</td>
            <td>
              <div class="col-cookie">
                <p>lang</p>
              </div>
            </td>
            <td>
              <div>This cookie is primarily used for tracking and targeting.</div>
            </td>
            <td>Through your browser settings</td>
          </tr>
          <tr>
            <td>Google</td>
            <td>
              <div class="col-cookie">
                <p>NID</p>
              </div>
            </td>
            <td>
              <div>
                This cookie contains a unique ID which Google uses to remember a user’s preferences
                and other information, such as your preferred language, how many search results you
                wish to have shown per page, and whether or not you wish to have Google’s SafeSearch
                filter turned on.
              </div>
            </td>
            <td>Through your browser settings</td>
          </tr>
          <tr>
            <td>Google</td>
            <td>
              <div class="col-cookie">
                <p>__gpi</p>
              </div>
            </td>
            <td>
              <div>
                Collects information on user behavior on multiple websites. This information is used
                to optimize the relevance of advertisements.
              </div>
            </td>
            <td>Through your browser settings</td>
          </tr>
          <tr>
            <td>Google Ad Manager</td>
            <td>
              <div class="col-cookie">
                <p>__gads</p>
                <p>GoogleAdServingTest</p>
              </div>
            </td>
            <td>
              <div>
                Peatix displays advertisements to users in certain locales and on certain pages on
                our site. These cookies are used by Google for the purpose of measuring the
                interactions with the ads on our site to determine the ads that are being shown to a
                user and to prevent the same ads from being shown to the same user too many times.
              </div>
            </td>
            <td>Through your browser settings</td>
          </tr>
          <tr>
            <td>Google DoubleClick</td>
            <td>
              <div class="col-cookie">
                <p>DSID</p>
                <p>IDE</p>
                <p>test_cookie</p>
              </div>
            </td>
            <td>
              <div>
                These cookies are used by Google DoubleClick to present targeted ads across the web
                to users based on their previous visits to a website. It measures the conversion
                rate of ads presented to the user.
              </div>
            </td>
            <td>
              <span>
                You can manage your ad preferences or opt out of interest-based Google ads from
                <a
                  href="https://myadcenter.google.com/home?sasb=true&ref=ad-settings"
                  target="_blank"
                >Google Ads Settings</a>. Alternatively, you can install a
                <a
                  href="https://support.google.com/My-Ad-Center-Help/answer/12155656?visit_id=638557108140760491-1284677924&rd=1"
                  target="_blank"
                >browser plugin</a>
                to keep your preference to opt out of personalized ads.
              </span>
            </td>
          </tr>
          <tr>
            <td>New Relic</td>
            <td>
              <div class="col-cookie">
                <p>JSESSIONID</p>
              </div>
            </td>
            <td>
              <div>For monitoring the performance of web and mobile applications.</div>
            </td>
            <td>Through your browser settings</td>
          </tr>
          <tr>
            <td>ShareThis</td>
            <td>
              <div class="col-cookie">
                <p>st_samesite</p>
              </div>
            </td>
            <td>
              <div>
                This cookie gathers information regarding the browsing and sharing activities of
                internet users that utilize the sharethis.com social media sharing functionality on
                this site.
              </div>
            </td>
            <td>
              <a
                href="https://sharethis.com/privacy/"
                target="_blank"
              >https://sharethis.com/privacy/</a>
            </td>
          </tr>
          <tr>
            <td>SpotX</td>
            <td>
              <div class="col-cookie">
                <p>audience</p>
              </div>
            </td>
            <td>
              <div>
                Used to determine whether video-advertisements have been displayed correctly on the
                website.
              </div>
            </td>
            <td>Through your browser settings</td>
          </tr>
          <tr>
            <td>Supership</td>
            <td>
              <div class="col-cookie">
                <p>SOC</p>
              </div>
            </td>
            <td>
              <div>Cookies may be used to allow targeted marketing and measurement.</div>
            </td>
            <td>Through your browser settings</td>
          </tr>
          <tr>
            <td>Treasure Data</td>
            <td>
              <div class="col-cookie">
                <p>_td_global</p>
              </div>
            </td>
            <td>
              <div>
                Registers statistical data on users' behavior on the website. Used for internal
                analytics by the website operator.
              </div>
            </td>
            <td>Through your browser settings</td>
          </tr>
          <tr>
            <td>Wufoo / SurveyMonkey</td>
            <td>
              <div class="col-cookie">
                <p>_splunk_rum_sid</p>
              </div>
            </td>
            <td>
              <div>
                Detects and logs potential errors on third-party provided functions on the website.
              </div>
            </td>
            <td>Through your browser settings</td>
          </tr>
          <tr>
            <td>Yahoo</td>
            <td>
              <div class="col-cookie">
                <p>A3</p>
                <p>IDSYNC</p>
                <p>_yjsu_yjad</p>
              </div>
            </td>
            <td>
              <div>Ads targeting cookies for Yahoo</div>
            </td>
            <td>Through your browser settings</td>
          </tr>
          <tr>
            <td>YouTube</td>
            <td>
              <div class="col-cookie">
                <p>GPS</p>
                <p>PREF</p>
                <p>VISITOR_INFO1_LIVE</p>
                <p>YSC</p>
                <p>VISITOR_PRIVACY_METADATA</p>
              </div>
            </td>
            <td>
              <div>
                Peatix enables organizers to embed YouTube videos onto their event or group pages to
                enrich the content on those pages. Google sets a number of cookies on any page that
                includes a YouTube video. These cookies allow YouTube to collect anonymous usage
                information such as user preferences, video usage based on geolocation, and views of
                embedded videos.
              </div>
            </td>
            <td>Through your browser settings</td>
          </tr>
          <tr>
            <td>YouTube</td>
            <td>
              <div class="col-cookie">
                <p>CONSENT</p>
              </div>
            </td>
            <td>
              <div>
                This cookie carries out information about how the user uses the website and any
                advertising that the user may have seen before visiting that said website.
              </div>
            </td>
            <td>Through your browser settings</td>
          </tr>
        </tbody>
      </VTable>
      <Divider />
    </article>
    <article>
      <h2 class="article-title">
        Social Media Cookies
      </h2>
      <p>
        These cookies are set by a range of social media services that we have added to the site to
        enable you to share our content with your friends and networks. They are capable of tracking
        your browser across other sites and building a profile of your interests. This may impact
        the content and messages you see on other websites you visit. If you do not allow these
        cookies, you may not be able to use or see these sharing tools.
      </p>
      <VTable class="table-cookie-policy">
        <thead>
          <tr>
            <template v-if="isMobile">
              <th colspan="4">
                <div class="header-sm">
                  <span>Service</span><span>/</span> <span>Cookie</span><span>/</span>
                  <span>Description</span><span>/</span>
                  <span>Opt-out</span>
                </div>
              </th>
            </template>
            <template v-else>
              <th><span>Service</span></th>
              <th><span>Cookie</span></th>
              <th><span>Description</span></th>
              <th><span>Opt-out</span></th>
            </template>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Facebook</td>
            <td>
              <div class="col-cookie">
                <p>fr</p>
                <p>tr</p>
                <p>_fbp</p>
              </div>
            </td>
            <td>
              <div>
                These cookies enable Facebook to follow your browsing behavior across other
                websites, to provide you with more personalized advertisements.
              </div>
            </td>
            <td>
              <a
                href="https://www.facebook.com/adpreferences/ad_settings"
                target="_blank"
              >https://www.facebook.com/adpreferences/ad_settings</a>
            </td>
          </tr>
          <tr>
            <td>X (formerly Twitter)</td>
            <td>
              <div class="col-cookie">
                <p>__widgetsettings</p>
                <p>i/jot</p>
                <p>local_storage_support_test</p>
              </div>
            </td>
            <td>
              <div>
                These cookies are used by X (formerly Twitter) to enable users to share content on
                their X (formerly Twitter) profiles using the Post (formerly Tweet) buttons on
                certain pages on our site. X (formerly Twitter) also collects data on user behavior
                and interaction to enable them to optimize their site and make advertisements on
                their site more relevant to users.
              </div>
            </td>
            <td>
              <a
                href="https://help.twitter.com/en/about-personalization-across-your-devices"
                target="_blank"
              >https://help.twitter.com/en/about-personalization-across-your-devices</a>
            </td>
          </tr>
        </tbody>
      </VTable>
      <Divider />
    </article>
    <article>
      <h2 class="article-title">
        Managing your Cookie Preferences
      </h2>
      <p>
        You can remove existing cookies from your device or block future cookies from being placed
        on your device by changing the settings in your browser. The links below contain information
        on how you can change your cookie settings on popular browsers.
      </p>
      <ul>
        <li>
          <a
            href="https://support.apple.com/en-gb/guide/safari/sfri11471/mac"
            target="_blank"
          >Apple Safari</a>
        </li>
        <li>
          <a
            href="https://support.google.com/chrome/answer/95647"
            target="_blank"
          >Google Chrome</a>
        </li>
        <li>
          <a
            href="https://support.microsoft.com/en-us/windows/manage-cookies-in-microsoft-edge-view-allow-block-delete-and-use-168dab11-0753-043d-7c16-ede5947fc64d"
            target="_blank"
          >Microsoft Edge</a>
        </li>
      </ul>
      <p>
        Please note that removing or blocking cookies may impact your user experience as parts of
        the site may no longer work.
      </p>
      <Divider />
    </article>
    <article>
      <h2 class="article-title">
        Changes to this Cookie Policy
      </h2>
      <p>
        We may update this Cookie Policy and add or remove cookies from time to time, and encourage
        you to review this policy periodically to stay informed about how we are using cookies.
      </p>
      <Divider />
    </article>
    <article>
      <TermsLastUpdatedSection display-date="June 25, 2024" />
    </article>
    <GetInTouch />
  </div>
</template>
<style lang="scss">
.v-table.table-cookie-policy {
  .v-table__wrapper {
    table {
      border-collapse: collapse;
    }

    thead tr th,
    tbody tr td {
      padding: spacings-get(4) spacings-get(6) spacings-get(4) 0;
      color: colors-get(gray, 600);

      &:last-child {
        padding-right: 0;
      }
    }

    thead {
      tr th {
        width: calc(100% / 4);

        span {
          @include fonts-get(medium, text-xs);
        }

        .header-sm {
          display: inline-flex;
          gap: spacings-get(2);
        }
      }
    }

    tbody {
      tr {
        td {
          vertical-align: top;
          @include fonts-get(regular, text-sm);

            > a {
                overflow-wrap: break-word;
            }

          .col-cookie {
            display: grid;
            gap: spacings-get(2);
          }

          @include media-query-max(mobile) {
            display: flex;
            height: fit-content;
          }
        }

        @include media-query-max(mobile) {
          &:not(:last-child) {
            border-bottom: thin solid #{colors-get(gray, 200)};
          }

          &:last-child td:last-child {
            margin-bottom: 0;
          }

          td {
            border-bottom: none !important;
            overflow-wrap: break-word;

            &:not(:first-child) {
              padding: spacings-get(2) spacings-get(6) spacings-get(2) spacings-get(2);
              background: colors-get(gray, 50);
            }

            &:nth-child(2) {
              border-top-left-radius: rem(8);
              border-top-right-radius: rem(8);
            }

            &:last-child {
              border-bottom-left-radius: rem(8);
              border-bottom-right-radius: rem(8);
              margin-bottom: spacings-get(4);
            }
          }
        }
      }
    }
  }
}
</style>
